import useSWR from 'swr'
import useSWRMutation, { SWRMutationConfiguration } from 'swr/mutation'

import { toApiPage, withQueryParams } from '@/utils'

import { CitiesParams, CitiesResponse, CreateCitySchema, UpdateCitySchema } from './types'
import { API } from '@/api/endpoints'
import { City } from '@/api/types'
import { FetchError, useSWRFetcher } from '@/lib/swr/fetcher'

const hasPagination = (data?: CitiesResponse) => {
  return (
    data &&
    data.current_page !== undefined &&
    data.last_page !== undefined &&
    data.per_page !== undefined &&
    data.total !== undefined
  )
}

/**
 * GET All
 */
export const useCities = (params: CitiesParams = {}) => {
  const swrKey = withQueryParams(API.CITIES, {
    page: params.page !== undefined ? toApiPage(params.page) : undefined,
    per_page: params.per_page,
    order_by: params.order_by,
    search: params.search,
    stats: 1
  })

  const { data, error, isLoading, mutate } = useSWR<CitiesResponse>(swrKey)
  const pagination = hasPagination(data) ? data : undefined

  const collection = params.onlyWithAdvertisements
    ? data?.data?.filter((city) => (city.advertisements_count ?? 0) > 0)
    : data?.data

  return {
    collection,
    pagination: pagination
      ? {
          current_page: pagination.current_page,
          last_page: pagination.last_page,
          per_page: pagination.per_page,
          total: pagination.total
        }
      : undefined,
    isLoading,
    error,
    mutate
  }
}

/**
 * GET
 */
export const useCity = (id: number | null) => {
  const { data, error, isLoading, mutate } = useSWR<City>(id ? `${API.CITIES}/${id}` : null)

  return {
    city: data,
    isLoading,
    error,
    mutate
  }
}

// ─── Create ──────────────────────────────────────────────────────────────────

type CreateCityOptions = SWRMutationConfiguration<
  City,
  FetchError,
  string,
  CreateCitySchema,
  City
> & { throwOnError?: boolean }

export const useCreateCity = (options?: CreateCityOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isCreating } = useSWRMutation(
    API.CITIES,
    (url: string, { arg }: { arg: CreateCitySchema }) =>
      fetcher<City>(url, { method: 'POST', body: JSON.stringify(arg) }),
    options
  )
  return { createCity: trigger, isCreating }
}

// ─── Update ──────────────────────────────────────────────────────────────────

type UpdateCityOptions = SWRMutationConfiguration<
  City,
  FetchError,
  string,
  { id: number; data: UpdateCitySchema },
  City
> & { throwOnError?: boolean }

export const useUpdateCity = (options?: UpdateCityOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isUpdating } = useSWRMutation(
    API.CITIES,
    (url: string, { arg }: { arg: { id: number; data: UpdateCitySchema } }) =>
      fetcher<City>(`${url}/${arg.id}`, { method: 'PUT', body: JSON.stringify(arg.data) }),
    options
  )
  return { updateCity: trigger, isUpdating }
}

// ─── Delete ──────────────────────────────────────────────────────────────────

type DeleteCityOptions = SWRMutationConfiguration<void, FetchError, string, number, void> & {
  throwOnError?: boolean
}

export const useDeleteCity = (options?: DeleteCityOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isDeleting } = useSWRMutation(
    API.CITIES,
    (url: string, { arg }: { arg: number }) => fetcher<void>(`${url}/${arg}`, { method: 'DELETE' }),
    options
  )
  return { deleteCity: trigger, isDeleting }
}
